import { PageMeta } from "@/components/PageMeta";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { PrimaryCTA, TextCTA, SoftCTA } from "@/components/CTA";
import { ExitIntentPrompt } from "@/components/ExitIntentPrompt";
import { ExecutivePresenceDiagnosticBanner } from "@/components/ExecutivePresenceDiagnosticBanner";
import { Tape } from "@/components/Notebook";
import { ROUTES, BRAND, LINKS } from "@/config/links";
import { useLang } from "@/i18n";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const ZAHOS_HREF = `${ROUTES.caseStudies}/zahos-xygas`;
const PANTELIS_HREF = `${ROUTES.caseStudies}/pantelis-papachristou`;

/**
 * Executive Presence Coaching — 1:1 service landing page.
 * Approved 11-section structure (do not reorder, do not add sections):
 *  1. Hero                     6. Main case study — Zahos (primary)
 *  2. Early client proof       7. Secondary case study — Pantelis
 *  3. Professional situations  8. Your coaching (practicals)
 *  4. What we work towards     9. Your coach
 *  5. How we work (method)    10. FAQ
 *                              11. Final CTA
 * Plus one extra promotional banner (`ExecutivePresenceDiagnosticBanner`,
 * between sections 7 and 8) + a Hero secondary CTA added 2026-02 at the
 * user's explicit request, mirroring exactly how Landing.jsx promotes the
 * Readiness Check on the sibling "High-Stakes Presentation Coaching" page.
 * Exactly 4 primary booking CTAs (hero / methodology / coaching_plan / final),
 * all routed to the dedicated /book/executive-presence booking page.
 * Zahos + Pantelis case-study links reuse the existing Insights CMS
 * case-study URLs — never invented.
 */
export default function ExecutivePresenceDevelopment() {
  const { t } = useLang();

  const situationsCards = t("epd.situations_cards") || [];
  const outcomesItems = t("epd.outcomes_items") || [];
  const methodSteps = t("epd.method_steps") || [];
  const planItems = t("epd.plan_items") || [];
  const faqItems = t("epd.faq_items") || [];
  const heroServiceNote = t("epd.hero_service_note");
  const finalServiceNote = t("epd.final_service_note");

  return (
    <>
      <PageMeta
        titleKey="meta.epd_title"
        descriptionKey="meta.epd_description"
        path={ROUTES.executivePresenceDevelopment}
      />

      {/* 1 — HERO */}
      <section className="relative z-10 pt-28 md:pt-36 pb-16 md:pb-24" data-testid="epd-hero">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5" data-testid="epd-hero-eyebrow">
              {t("epd.hero_eyebrow")}
            </p>
            <h1
              className="font-serif text-charcoal leading-[1.05] text-4xl sm:text-5xl lg:text-6xl tracking-tight"
              data-testid="epd-hero-h1"
            >
              {t("epd.hero_h1")}
            </h1>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[56ch]" data-testid="epd-hero-description">
              {t("epd.hero_description")}
            </p>
            {heroServiceNote && (
              <p className="mt-3 font-body text-sm text-ink-soft italic" data-testid="epd-hero-service-note">
                {heroServiceNote}
              </p>
            )}
            <div className="mt-8">
              <PrimaryCTA href={ROUTES.bookExecutivePresence} testId="epd-hero-book-cta" location="hero" targetAssetId="booking_executive_presence">
                {t("epd.hero_cta_book")}
              </PrimaryCTA>
            </div>
            <p className="mt-4 font-body text-sm text-ink-soft max-w-[52ch]" data-testid="epd-hero-microcopy">
              {t("epd.hero_microcopy")}
            </p>
            <div className="mt-5">
              <SoftCTA href={LINKS.epDiagnostic} testId="epd-hero-diagnostic-cta" location="hero" tone="light" targetAssetId="executive_presence_communication">
                {t("epd.hero_diagnostic_cta")}
              </SoftCTA>
              <p className="mt-2 font-body text-sm text-ink-soft" data-testid="epd-hero-diagnostic-helper">
                {t("epd.hero_diagnostic_helper")}
              </p>
            </div>
            <a
              href="#epd-how-we-work"
              data-testid="epd-hero-explore-link"
              className="group mt-5 inline-flex items-center gap-2 font-body text-sm text-ink-soft underline decoration-terracotta/50 underline-offset-4 transition-colors duration-300 hover:text-terracotta"
            >
              <span>{t("epd.hero_cta_explore")}</span>
              <span className="transition-transform duration-300 group-hover:translate-x-1">&rarr;</span>
            </a>
          </div>

          <div className="lg:col-span-5">
            <div className="relative max-w-md mx-auto lg:mx-0">
              <Tape className="absolute -top-4 left-6 z-10" rotate={-5} width={90} />
              <Tape className="absolute -top-4 right-6 z-10" rotate={4} width={90} />
              <div className="bg-paper border border-ink/12 shadow-[6px_12px_36px_rgba(27,27,24,0.10)] p-3" data-testid="epd-hero-image">
                <img
                  src="/sketches/epd-presence.webp"
                  alt={t("epd.hero_image_alt")}
                  className="w-full h-auto block"
                  style={{ mixBlendMode: "multiply" }}
                  draggable={false}
                />
              </div>
              <p className="mt-3 font-hand text-pencil text-lg text-center">
                {t("epd.hero_image_caption")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2 — EARLY CLIENT PROOF (Zahos, compact) */}
      <section className="relative z-10 py-14 md:py-16 border-y border-ink/10 bg-paper-deep/30" data-testid="epd-proof">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="border-l-2 border-terracotta/60 pl-6 md:pl-8">
              <blockquote className="font-serif italic text-xl md:text-2xl text-ink leading-snug" data-testid="epd-proof-quote">
                &ldquo;{t("epd.proof_quote")}&rdquo;
              </blockquote>
              <p className="mt-3 font-hand text-terracotta text-lg" data-testid="epd-proof-client">
                {t("epd.proof_client")} · {t("epd.proof_role")}
              </p>
              <p className="mt-3 font-body text-ink-soft leading-relaxed max-w-[58ch]">
                {t("epd.proof_body")}
              </p>
              <div className="mt-4">
                <TextCTA href={ZAHOS_HREF} testId="epd-proof-link">
                  {t("epd.proof_link")}
                </TextCTA>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3 — PROFESSIONAL SITUATIONS */}
      <section className="relative z-10 py-24 md:py-32" data-testid="epd-situations">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <Reveal className="mb-12 md:mb-16 max-w-2xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("epd.situations_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("epd.situations_title")}
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed">
              {t("epd.situations_description")}
            </p>
          </Reveal>

          <StaggerGroup className="grid sm:grid-cols-2 gap-5 md:gap-6">
            {situationsCards.map((c, i) => (
              <StaggerItem key={c.title}>
                <div
                  className="bg-paper border border-ink/12 p-7 md:p-8 h-full"
                  data-testid={`epd-situation-card-${i}`}
                >
                  <h3 className="font-serif text-xl md:text-2xl text-ink leading-tight mb-3">{c.title}</h3>
                  <p className="font-body text-ink-soft leading-relaxed">{c.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* 4 — WHAT WE WORK TOWARDS */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10" data-testid="epd-outcomes">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <Reveal className="mb-12 md:mb-16 flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-10">
            <div className="max-w-2xl">
              <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
                {t("epd.outcomes_eyebrow")}
              </p>
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
                {t("epd.outcomes_title")}
              </h2>
              <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed">
                {t("epd.outcomes_description")}
              </p>
            </div>
            <img
              src="/sketches/epd-pressure.webp"
              alt={t("epd.outcome_pressure_alt")}
              loading="lazy"
              className="w-full max-w-md h-auto border border-ink/12 bg-paper p-1 shrink-0"
              style={{ mixBlendMode: "multiply" }}
              draggable={false}
              data-testid="epd-outcome-pressure-image"
            />
          </Reveal>

          <StaggerGroup className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
            {outcomesItems.map((o, i) => (
              <StaggerItem key={o.title}>
                <div className="border-t border-ink/15 pt-5" data-testid={`epd-outcome-item-${i}`}>
                  <span className="font-hand text-terracotta text-2xl leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-2 font-serif text-xl md:text-2xl text-ink leading-tight">{o.title}</h3>
                  <p className="mt-2 font-body text-ink-soft leading-relaxed">{o.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* 5 — HOW WE WORK (core methodology, always visible, never accordion/carousel) */}
      <section id="epd-how-we-work" className="relative z-10 py-24 md:py-32" data-testid="epd-method">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <Reveal className="mb-12 md:mb-16 flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-10">
            <div className="max-w-2xl">
              <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
                {t("epd.method_eyebrow")}
              </p>
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
                {t("epd.method_title")}
              </h2>
              <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed">
                {t("epd.method_description")}
              </p>
            </div>
            <img
              src="/sketches/epd-breath.webp"
              alt={t("epd.method_breath_alt")}
              loading="lazy"
              className="w-full max-w-md h-auto border border-ink/12 bg-paper p-1 shrink-0"
              style={{ mixBlendMode: "multiply" }}
              draggable={false}
              data-testid="epd-method-breath-image"
            />
          </Reveal>

          <StaggerGroup className="grid md:grid-cols-2 gap-6 md:gap-8 mb-10">
            {methodSteps.map((s, i) => (
              <StaggerItem key={s.title}>
                <div className="bg-paper border border-ink/12 p-7 md:p-8 h-full" data-testid={`epd-method-step-${i}`}>
                  <span className="font-hand text-terracotta text-2xl leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-3 font-serif text-xl md:text-2xl text-ink leading-tight">{s.title}</h3>
                  <p className="mt-3 font-body text-ink-soft leading-relaxed">{s.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>

          <Reveal>
            <p className="font-serif italic text-xl md:text-2xl text-ink leading-snug max-w-2xl mb-8">
              {t("epd.method_supporting")}
            </p>
            <PrimaryCTA href={ROUTES.bookExecutivePresence} testId="epd-method-book-cta" location="methodology" targetAssetId="booking_executive_presence">
              {t("epd.method_cta")}
            </PrimaryCTA>
          </Reveal>
        </div>
      </section>

      {/* 6 — MAIN CASE STUDY: ZAHOS (primary) */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10" data-testid="epd-case-zahos">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("epd.zahos_eyebrow")}
            </p>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight text-ink max-w-[26ch]">
              {t("epd.zahos_title")}
            </h2>
            <div className="mt-6 space-y-4 font-body text-ink-soft leading-relaxed max-w-[62ch]">
              <p>{t("epd.zahos_description_a")}</p>
              <p>{t("epd.zahos_description_b")}</p>
              <p>{t("epd.zahos_description_c")}</p>
            </div>
            <div className="mt-8 border-l-2 border-terracotta pl-6 max-w-[58ch]">
              <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-2">
                {t("epd.zahos_subheading")}
              </p>
              <p className="font-serif text-xl md:text-2xl text-ink leading-snug">{t("epd.zahos_outcome")}</p>
            </div>
            <p className="mt-6 font-hand text-terracotta text-lg">{t("epd.zahos_client")}</p>
            <div className="mt-5">
              <TextCTA href={ZAHOS_HREF} testId="epd-case-zahos-link">
                {t("epd.zahos_link")}
              </TextCTA>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 7 — SECONDARY CASE STUDY: PANTELIS (visually lighter than Zahos) */}
      <section className="relative z-10 py-14 md:py-16 border-b border-ink/10" data-testid="epd-case-pantelis">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-ink-soft mb-3">
              {t("epd.pantelis_eyebrow")}
            </p>
            <h3 className="font-serif text-lg md:text-xl text-ink leading-snug mb-3 max-w-[32ch]">
              {t("epd.pantelis_title")}
            </h3>
            <div className="space-y-3 font-body text-sm text-ink-soft leading-relaxed max-w-[58ch]">
              <p>{t("epd.pantelis_description_a")}</p>
              <p>{t("epd.pantelis_description_b")}</p>
              <p>{t("epd.pantelis_description_c")}</p>
            </div>
            <p className="mt-4 font-body text-xs uppercase tracking-[0.2em] text-terracotta">
              {t("epd.pantelis_subheading")}
            </p>
            <p className="mt-1 font-body text-sm text-ink leading-relaxed max-w-[58ch]">
              {t("epd.pantelis_outcome")}
            </p>
            <p className="mt-3 font-hand text-terracotta text-sm">{t("epd.pantelis_client")}</p>
            <div className="mt-3">
              <TextCTA href={PANTELIS_HREF} testId="epd-case-pantelis-link">
                {t("epd.pantelis_link")}
              </TextCTA>
            </div>
          </Reveal>
        </div>
      </section>

      <ExecutivePresenceDiagnosticBanner />

      {/* 8 — YOUR COACHING (practicals, no price/fixed sessions) */}
      <section className="relative z-10 py-24 md:py-32" data-testid="epd-plan">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <Reveal className="mb-12 md:mb-16 max-w-2xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("epd.plan_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("epd.plan_title")}
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed">
              {t("epd.plan_description")}
            </p>
          </Reveal>

          <StaggerGroup className="grid sm:grid-cols-2 gap-x-10 gap-y-8 mb-12">
            {planItems.map((p, i) => (
              <StaggerItem key={p.title}>
                <div className="border-t border-ink/15 pt-5" data-testid={`epd-plan-item-${i}`}>
                  <h3 className="font-serif text-xl md:text-2xl text-ink leading-tight">{p.title}</h3>
                  <p className="mt-2 font-body text-ink-soft leading-relaxed">{p.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>

          <Reveal>
            <p className="font-hand text-terracotta text-xl md:text-2xl mb-8" data-testid="epd-plan-practicals">
              {t("epd.plan_practicals")}
            </p>
            <PrimaryCTA href={ROUTES.bookExecutivePresence} testId="epd-plan-book-cta" location="coaching_plan" targetAssetId="booking_executive_presence">
              {t("epd.plan_cta")}
            </PrimaryCTA>
            <p className="mt-4 font-body text-sm text-ink-soft max-w-[52ch]">
              {t("epd.plan_microcopy")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* 9 — YOUR COACH */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10" data-testid="epd-coach">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-4 order-2 lg:order-1">
            <div className="relative max-w-[220px] lg:max-w-[260px] mx-auto lg:mx-0">
              <div className="aspect-[4/5] bg-paper border border-ink/12 overflow-hidden shadow-[6px_12px_36px_rgba(27,27,24,0.10)]">
                <img
                  src="/sketches/coach-portrait.webp"
                  srcSet="/sketches/coach-portrait-500.webp 500w, /sketches/coach-portrait.webp 900w"
                  sizes="(min-width: 1024px) 24vw, 60vw"
                  loading="lazy"
                  alt={t("about.portrait_alt", { name: BRAND.coach })}
                  className="w-full h-full object-cover object-[center_top]"
                  style={{ mixBlendMode: "multiply", filter: "contrast(1.05)" }}
                  draggable={false}
                  data-testid="epd-coach-portrait"
                />
              </div>
              <p className="mt-3 font-hand text-terracotta text-lg text-center lg:text-left">
                {BRAND.coach}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-8 order-1 lg:order-2">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("epd.coach_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink mb-6">
              {t("epd.coach_title")}
            </h2>

            {/* Desktop bio — full paragraphs */}
            <div className="hidden md:block space-y-5 font-body text-lg text-ink-soft leading-relaxed max-w-[62ch]">
              <p>{t("epd.coach_body_a")}</p>
              <p>{t("epd.coach_body_b")}</p>
              <p>{t("epd.coach_body_c")}</p>
              <p>{t("epd.coach_body_d")}</p>
            </div>

            {/* Mobile bio — shorter approved version */}
            <div className="md:hidden space-y-4 font-body text-ink-soft leading-relaxed">
              <p>{t("epd.coach_body_mobile_a")}</p>
              <p>{t("epd.coach_body_mobile_b")}</p>
              <p>{t("epd.coach_body_mobile_c")}</p>
            </div>

            <p className="mt-6 font-body text-sm text-ink-soft" data-testid="epd-coach-credential">
              {t("epd.coach_credential")}
            </p>
            <div className="mt-5">
              <TextCTA href={ROUTES.about} testId="epd-coach-link">
                {t("epd.coach_link")}
              </TextCTA>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 10 — FAQ */}
      <section className="relative z-10 py-24 md:py-36" data-testid="epd-faq">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-2xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("epd.faq_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
              {t("epd.faq_title")}
            </h2>
          </Reveal>

          <Reveal>
            <Accordion type="multiple" className="w-full border-t border-ink/15" data-testid="epd-faq-accordion">
              {faqItems.map((f, i) => (
                <AccordionItem key={i} value={`epd-faq-${i}`} className="border-ink/15">
                  <AccordionTrigger
                    data-testid={`epd-faq-trigger-${i}`}
                    className="font-serif text-xl md:text-2xl text-ink hover:no-underline py-6 [&>svg]:text-terracotta [&>svg]:h-5 [&>svg]:w-5 text-left"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <p className="font-body text-base md:text-lg text-ink-soft leading-relaxed max-w-2xl">
                      {f.a}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* 11 — FINAL CTA */}
      <section className="relative z-10 py-24 md:py-32 bg-ink text-paper" data-testid="epd-final-cta">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-mustard mb-6">
              {t("epd.final_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-paper">
              {t("epd.final_title")}
            </h2>
            <p className="mt-6 font-body text-lg text-paper/90 max-w-2xl mx-auto leading-relaxed">
              {t("epd.final_description")}
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-10">
            <PrimaryCTA href={ROUTES.bookExecutivePresence} testId="epd-final-book-cta" location="final" targetAssetId="booking_executive_presence">
              {t("epd.final_cta")}
            </PrimaryCTA>
          </Reveal>
          <Reveal delay={0.25} className="mt-6">
            <p className="font-body text-sm text-paper/75 max-w-xl mx-auto" data-testid="epd-final-microcopy">
              {t("epd.final_microcopy")}
            </p>
            {finalServiceNote && (
              <p className="mt-2 font-body text-sm text-paper/75 italic" data-testid="epd-final-service-note">
                {finalServiceNote}
              </p>
            )}
          </Reveal>
        </div>
      </section>

      <ExitIntentPrompt variant="executive_presence" />
    </>
  );
}
